import React from 'react';

interface PlanCardProps {
  name: string;
  price: number;
  credits: number;
  pricePerCredit: number;
  features: readonly string[];
  selected: boolean;
  onSelect: () => void;
  /** Badge label shown above card (e.g. "Popular", "★ Limited") */
  badge?: string;
  /** Override accent colour (default: purple) */
  accentColor?: string;
}

/** Format cents to AUD display string (no decimals). */
const fmtPrice = (cents: number) =>
  (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/**
 * A single billing / credit-package card used in StripeBilling.
 * Handles badge, selection state, feature list.
 */
const PlanCard: React.FC<PlanCardProps> = ({
  name,
  price,
  credits,
  pricePerCredit,
  features,
  selected,
  onSelect,
  badge,
  accentColor = '#5B21B6',
}) => (
  <div
    onClick={onSelect}
    data-testid={`plan-card-${name.toLowerCase()}`}
    style={{
      border: `2px solid ${selected ? accentColor : '#e0e0e0'}`,
      borderRadius: 12,
      padding: 24,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: selected ? 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)' : 'white',
      position: 'relative',
      overflow: 'visible',
      marginTop: badge ? 16 : 0,
      boxShadow: selected ? '0 8px 24px rgba(15, 58, 125, 0.15)' : undefined,
    }}
  >
    {/* Selection checkmark */}
    {selected && (
      <span style={{
        position: 'absolute', top: 12, right: 12,
        width: 24, height: 24,
        background: '#0f3a7d', color: 'white',
        borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: 14,
      }}>✓</span>
    )}

    {/* Badge */}
    {badge && (
      <div style={{
        position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
        background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
        color: 'white', padding: '6px 16px', borderRadius: 16,
        fontSize: 11, fontWeight: 700,
        boxShadow: `0 2px 8px ${accentColor}4d`,
        whiteSpace: 'nowrap', zIndex: 10,
      }}>
        {badge}
      </div>
    )}

    <div style={{
      fontSize: 18, fontWeight: 600, color: '#0f3a7d',
      marginBottom: 8, marginTop: badge ? 8 : 0,
    }}>{name}</div>

    <div style={{ fontSize: 28, fontWeight: 700, color: accentColor, marginBottom: 4 }}>
      ${fmtPrice(price)}
    </div>
    <div style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
      {credits.toLocaleString()} credits · ${fmtPrice(pricePerCredit)}/ea
    </div>

    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {features.map((f, i) => (
        <li key={i} style={{
          fontSize: 12, color: '#555', padding: '6px 0',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{ color: '#28a745', fontWeight: 'bold' }}>✓</span>
          {f}
        </li>
      ))}
    </ul>
  </div>
);

export default PlanCard;
